import { inject } from '@angular/core'

import { lastValueFrom } from 'rxjs'

import { FlxModalService } from '@flowx/angular-ui-toolkit'
import {
  ExportBinariesComponent,
  ExportModalData,
} from '@flowx/designer/modules/projects/components/export-binaries/export-binaries.component'
import { RuntimeApiService } from '@flowx/designer/shared/api/runtime.api'
import { ExportType } from '@flowx/designer/shared/entities/application.entity'
import { downloadHttpResponse } from '@flowx/shared/utils'

import { CHANGE_TARGET_VERSION_MODAL_CONFIG, injectExportReview } from './export-review'
import { injectWithErrorToast } from './with-error-toast'

import { ExportReviewOption } from '../export-review-modal/export-review-modal.component'
import {
  ExtendedNotification,
  NotificationActionHandler,
  NotificationActionType,
  NotificationType,
} from '../notifications.model'

type BuildExportActionType =
  | NotificationActionType.RETRY_BUILD_EXPORT
  | NotificationActionType.REVIEW_BUILD_EXPORT
  | NotificationActionType.DOWNLOAD_BUILD_EXPORT

type BuildExportNotification = Extract<
  ExtendedNotification,
  { type: NotificationType.BUILD_EXPORT }
>

/**
 * The actions a build export notification offers, the build counterparts of the app version export's.
 * A build is addressed by its app version, which the notification carries as `appVersionId`. Must be
 * called in an injection context, for the same reason as `injectAppVersionExportHandlers`. Each
 * action runs in the workspace the export ran in, wherever the user is now.
 */
export const injectBuildExportHandlers = (): Record<
  BuildExportActionType,
  NotificationActionHandler
> => {
  const runtimeApi = inject(RuntimeApiService)
  const modalService = inject(FlxModalService)
  const openExportReview = injectExportReview()
  const withErrorToast = injectWithErrorToast()

  // Re-runs straight away with the original parameters; the fresh run reports through a notification of its own.
  const retryExport = (notification: BuildExportNotification): Promise<void> =>
    withErrorToast('Failed to export build', () =>
      lastValueFrom(
        runtimeApi.exportBuild(
          notification.appId,
          notification.appVersionId,
          true,
          notification.payload.flowxVersion,
          notification.payload.includeBinaries,
          notification.workspaceId
        )
      )
    )

  const reviewExport = (notification: BuildExportNotification): void =>
    openExportReview(notification, {
      [ExportReviewOption.DOWNLOAD]: () => downloadExport(notification),
      [ExportReviewOption.CHANGE_TARGET_VERSION]: () => changeTargetVersion(notification),
    })

  const downloadExport = (notification: BuildExportNotification): Promise<void> =>
    withErrorToast('Failed to download build', async () => {
      const response = await lastValueFrom(
        runtimeApi.downloadBuild(
          notification.appId,
          notification.appVersionId,
          notification.payload.flowxVersion,
          notification.payload.includeBinaries,
          notification.workspaceId
        )
      )

      downloadHttpResponse(response, `${notification.appName ?? notification.appId}.zip`)
    })

  // The export modal loads the build's target versions and history and runs the export or the
  // download itself; a notification carries no build tag, so its subtitle names none.
  const changeTargetVersion = (notification: BuildExportNotification): void => {
    modalService.show(ExportBinariesComponent, CHANGE_TARGET_VERSION_MODAL_CONFIG, {
      exportType: ExportType.BUILD,
      build: {
        workspaceId: notification.workspaceId,
        appId: notification.appId,
        applicationVersionId: notification.appVersionId,
        appName: notification.appName,
      },
    } as ExportModalData)
  }

  // Always matches, as in `injectAppVersionExportHandlers`; it is there so the handlers get the payload without a cast.
  const forBuildExport =
    (run: (notification: BuildExportNotification) => void): NotificationActionHandler =>
    (notification) => {
      if (notification.type === NotificationType.BUILD_EXPORT) {
        run(notification)
      }
    }

  return {
    [NotificationActionType.RETRY_BUILD_EXPORT]: forBuildExport(retryExport),
    [NotificationActionType.REVIEW_BUILD_EXPORT]: forBuildExport(reviewExport),
    [NotificationActionType.DOWNLOAD_BUILD_EXPORT]: forBuildExport(downloadExport),
  }
}
